/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getZonasDeSalariosMinimos,
  postZonaDeSalarioMinimo,
  deleteZonaDeSalarioMinimo,
  patchZonaDeSalarioMinimo,
  getZonaDeSalarioMinimo,
} from 'api/catalogos/zonasDeSalariosMinimos';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
}
  from 'utils/constants';
import { CheckCircleOutlined } from '@ant-design/icons';
import ModalConfirmation from 'components/ModalConfirmation';
import { NOMBRES_PATTERN_GENERICO } from 'utils/patterns';

export const zonaDeSalarioMinimoPermission = {
  permissionModel: 'zonadesalariominimo',
  catalogsModels: [],
  tabsModels: [],
};

const ZonaDeSalarioMinimo = ({ permission }) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [visibleAuthConfirmation, setVisibleAuthConfirmation] = useState(false);
  const [visibleAlertInactive, setVisibleAlertInactive] = useState(false);

  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getZonasDeSalariosMinimos();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getZonasDeSalariosMinimos(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableForm(false);
    form.resetFields();
    formDatos.resetFields();
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Autorizar un Sindicato ya Autorizado');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchZonaDeSalarioMinimo(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleAuthConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableForm(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _zonaDeSalarioMinimo = await getZonaDeSalarioMinimo(match.id);
    setTimeout(() => {
      if (_zonaDeSalarioMinimo) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          isDisabled(_zonaDeSalarioMinimo?.estados_globales);
          form.setFieldsValue(formatReceived(_zonaDeSalarioMinimo));
          formDatos.setFieldsValue(formatReceived(_zonaDeSalarioMinimo));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchZonaDeSalarioMinimo(key, values) : postZonaDeSalarioMinimo(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteZonaDeSalarioMinimo(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDeleteDisabled(isAutorizado);
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    concepto: [requiredRule],
    descripcion: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN_GENERICO,
        message: 'Ingrese un nombre válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
          customActions={[
            {
              onClick: () => setVisibleAuthConfirmation(true),
              permission: { model: 'zonadesalariominimo', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              // eslint-disable-next-line max-len
              disabled: !selectedRowKeys?.length || disableAuth,
            },
          ]}
          disableDelete={
            deleteDisabled
          }
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Zona de Salario Mínimo`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!selectedRowKeys?.length || !disableAuth}
            />
            )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            disabled={disableForm}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descripcion"
                rules={rules.descripcion}
                label="Descripción"
                hasFeedback
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  activeInactive={!deleteDisabled}
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                  onChange={(val) => {
                    if (val === ESTATUS_INACTIVO) {
                      setVisibleAlertInactive(true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Zona de Salario Mínimo ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleAlertInactive(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleAlertInactive(false);
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
          }}
          open={visibleAlertInactive}
          contentTitle={`Inactivar la Zona de Salario Minimo ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAuthConfirmation(false);
            onCancel();
          }}
          open={visibleAuthConfirmation}
          contentTitle={`Autorizar la Zona de Salario Minimo ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ZonaDeSalarioMinimo;
